import { computed, getCurrentInstance, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

// Magiclink request flow (screens: email -> channel -> sent, plus "restricted").
// Ported from quasar.sowell-extension.auth/src/composables/useMagicLink.ts and
// adapted to the V1 API (see readApiFailure below) - this app does not use the
// extension, so the logic lives here.

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const AVAILABLE_CHANNELS = ['email', 'sms']

// The V1 API advertises "sms" for any user who happens to have `recipients` on
// file, even though the product does not send magiclink SMS yet. Same choice as
// SoClean Manager v1 (MAGICLINK_CHANNELS: "email").
const ALLOWED_CHANNELS = ['email']

// The link is built against the public domain, never window.location.origin: in a
// Capacitor build the WebView origin is capacitor://localhost, which would produce
// an unusable link. Same constant as Proxy.resetPassword's `base`.
const APP_BASE_URL = 'https://reporter.sowellapp.com'
const SET_PASSWORD_PATH = '/set-password'

const RESEND_COUNTDOWN_SECONDS = 58

const WEBMAIL_INBOX_BY_DOMAIN = {
  'gmail.com': 'https://mail.google.com/mail/u/0/#inbox',
  'outlook.com': 'https://outlook.live.com/mail/0/inbox',
  'hotmail.com': 'https://outlook.live.com/mail/0/inbox',
  'live.com': 'https://outlook.live.com/mail/0/inbox',
  'yahoo.com': 'https://mail.yahoo.com',
  'icloud.com': 'https://www.icloud.com/mail'
}

const parseChannels = (entries) => {
  const channels = []
  const targets = {}

  for (const entry of entries || []) {
    const name = typeof entry === 'string' ? entry : entry && entry.channel
    if (!name || !AVAILABLE_CHANNELS.includes(name)) continue

    channels.push(name)
    const target = typeof entry === 'string' ? undefined : entry && entry.target
    if (target) targets[name] = target
  }

  return { channels, targets }
}

// V1 error payload is `{ errors: [{ id, title, code }] }`: no `detail`, and `title`
// carries an English business message ("The requested channel is not available for
// this user"). Never surface it - map the status/code to a translated key instead.
export const readApiFailure = (error) => {
  const response = (error && error.response) || {}
  const payload = response.data || {}
  const errors = Array.isArray(payload) ? payload : payload.errors || []
  const first = errors[0] || {}
  return { status: Number(response.status) || 0, code: first.code || '' }
}

export function useMagicLink() {
  const { t } = useI18n()
  const router = useRouter()
  const route = useRoute()
  // $SowellProxy is installed on globalProperties by boot/sowell.js.
  const proxy =
    getCurrentInstance().appContext.config.globalProperties.$SowellProxy

  const email = ref('')
  const emailError = ref('')
  const apiErrorMessage = ref('')
  const channels = ref([])
  const channelTargets = ref({})
  const selectedChannel = ref('email')
  const step = ref('email')
  const isLoading = ref(false)
  const resendCountdown = ref(0)
  const restricted = ref(false)
  let countdownTimer

  // "activate" (login page's "Activer mon compte" button) vs. bare (forgotten
  // password) - same page, only the copy of the email step differs.
  const isActivateContext = route.query.context === 'activate'

  const maskEmail = (value) => {
    const [local, domain] = (value || '').split('@')
    if (!local || !domain) return value
    return `${local.charAt(0)}***@${domain}`
  }

  const maskedEmail = computed(() => maskEmail(email.value))

  const validateEmail = () => {
    const value = email.value.trim()
    if (!value) {
      emailError.value = t('login.errorValidationEmail')
      return false
    }
    if (!EMAIL_PATTERN.test(value)) {
      emailError.value = t('login.errorValidationEmail')
      return false
    }
    emailError.value = ''
    return true
  }

  // V1 answers 405 (ActionController::MethodNotAllowed) where V2 answers 403 when
  // the company is listed in COMPANY_RESET_PASSWORD_FORBIDDEN. Listening to 403
  // only - as the extension does - would never show the "restricted" screen here.
  const isRestrictedStatus = (status) => status === 405 || status === 403

  const handleFailure = (error) => {
    const { status, code } = readApiFailure(error)

    if (isRestrictedStatus(status)) {
      restricted.value = true
      return
    }
    if (status === 404 || code === 'not_found') {
      apiErrorMessage.value = t('login.errorMagicLinkAccountNotFound')
      return
    }
    if (status === 422) {
      apiErrorMessage.value = t('login.errorUnprocessableRequest')
      return
    }
    apiErrorMessage.value = t('login.errorMagicLinkGeneric')
  }

  const requestChannels = async () => {
    apiErrorMessage.value = ''
    if (!validateEmail()) return

    isLoading.value = true
    try {
      const response = await proxy.requestMagicLink(email.value.trim())
      const data = (response && response.data) || {}
      const { channels: available, targets } = parseChannels(data.channels)
      const offered = available.filter((channel) =>
        ALLOWED_CHANNELS.includes(channel)
      )
      channels.value = offered.length ? offered : ['email']
      channelTargets.value = targets
      selectedChannel.value = 'email'
      step.value = 'channel'
    } catch (error) {
      handleFailure(error)
    } finally {
      isLoading.value = false
    }
  }

  const stopCountdown = () => {
    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = undefined
    }
  }

  const startCountdown = () => {
    resendCountdown.value = RESEND_COUNTDOWN_SECONDS
    stopCountdown()
    countdownTimer = setInterval(() => {
      if (resendCountdown.value > 0) {
        resendCountdown.value -= 1
      } else {
        stopCountdown()
      }
    }, 1000)
  }

  const sendMagicLink = async (channel) => {
    if (channel) selectedChannel.value = channel
    apiErrorMessage.value = ''
    isLoading.value = true
    try {
      await proxy.requestMagicLink(
        email.value.trim(),
        selectedChannel.value,
        APP_BASE_URL + SET_PASSWORD_PATH
      )
      step.value = 'sent'
      startCountdown()
    } catch (error) {
      handleFailure(error)
    } finally {
      isLoading.value = false
    }
  }

  const resend = () => {
    if (resendCountdown.value > 0 || isLoading.value) return
    sendMagicLink()
  }

  const selectChannel = (channel) => {
    selectedChannel.value = channel
  }

  const goBackToLogin = () => {
    router.push({ path: '/login' })
  }

  const openMailbox = () => {
    if (typeof window === 'undefined') return
    const domain = (email.value.split('@')[1] || '').toLowerCase()
    const inboxUrl = WEBMAIL_INBOX_BY_DOMAIN[domain]
    if (inboxUrl) {
      window.open(inboxUrl, '_blank')
    } else {
      window.location.href = `mailto:${email.value}`
    }
  }

  onUnmounted(stopCountdown)

  return {
    email,
    emailError,
    apiErrorMessage,
    channels,
    channelTargets,
    selectedChannel,
    step,
    isLoading,
    resendCountdown,
    restricted,
    isActivateContext,
    maskedEmail,
    maskEmail,
    requestChannels,
    sendMagicLink,
    resend,
    selectChannel,
    goBackToLogin,
    openMailbox
  }
}
